import { useJsonFile } from './useJsonFile'

/// Per-reserve rate/liquidity history produced by `scripts/reserveHistoryIndexer.cjs` - a plain
/// static file under public/, the same "off-chain indexer writes public/*.json, frontend reads it
/// once per mount" shape `useVaultNavHistory` and `usePointsData` use (via the shared `useJsonFile`).
///
/// **Inherits the no-fabricated-data rule.** A fresh deployment has one point, or none, and the
/// charts render a dash rather than a padded line - the same convention `useHistoricalStats` and
/// `useVaultNavHistory` both hold. Two points is the minimum a line can honestly be drawn from,
/// and `seriesFor` enforces it rather than leaving each caller to remember.
export function useReserveHistory() {
  const { data, loading } = useJsonFile('/reserve-history.json', { cache: 'no-store' })

  /// Points for one reserve inside a trailing window, oldest first.
  ///
  /// `windowDays` of 0 means everything recorded. Returns an empty array whenever there is too
  /// little to plot, so a caller can branch on `.length` alone.
  function seriesFor(asset, windowDays = 30) {
    if (!data || !asset) return []
    const points = data[asset.toLowerCase()]
    if (!Array.isArray(points) || points.length < 2) return []

    const cutoff = windowDays > 0 ? Date.now() - windowDays * 86_400_000 : 0
    const windowed = points.filter((p) => p.t >= cutoff)
    // A window that excludes all but one point would draw a line through nothing. Falling back to
    // the full series is more useful than an empty chart, and is honest as long as the axis labels
    // show the real dates - which they do, since ticks land on real observations.
    const chosen = windowed.length >= 2 ? windowed : points
    return [...chosen].sort((a, b) => a.t - b.t)
  }

  return { data, loading, seriesFor }
}
